import React, { useState, useMemo } from "react";
import Layout from "@/components/Layout";
import Seo, { useLocalBusinessLd } from "@/components/Seo";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { TrendingUp, ArrowRight, CheckCircle2, Info } from "lucide-react";

// Simple, editable revenue model: avg nightly rate by bedrooms x occupancy x 30 nights, minus commission.
const NIGHTLY = { "0": 62, "1": 78, "2": 98, "3": 128, "4": 165 };
const ZONE_FACTOR = { "rive-droite": 1.08, "rive-gauche": 1.0, "peripherie": 0.92 };
const OCCUPANCY = 0.7;
const COMMISSION = 0.2;

export default function RevenueEstimator() {
  const [bedrooms, setBedrooms] = useState("1");
  const [zone, setZone] = useState("rive-droite");
  const [lead, setLead] = useState({ first_name: "", last_name: "", phone: "", email: "",
    address: "", postal_code: "", property_type: "Appartement", surface: "", capacity: "", parking: "non" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setLead((p) => ({ ...p, [k]: e.target.value }));
  const setV = (k) => (v) => setLead((p) => ({ ...p, [k]: v }));

  const est = useMemo(() => {
    const base = NIGHTLY[bedrooms] ?? NIGHTLY["4"];
    const nightly = base * (ZONE_FACTOR[zone] ?? 1);
    const gross = nightly * 30 * OCCUPANCY;
    const net = gross * (1 - COMMISSION);
    const low = Math.round((net * 0.88) / 10) * 10;
    const high = Math.round((net * 1.12) / 10) * 10;
    return { low, high, gross: Math.round(gross) };
  }, [bedrooms, zone]);

  const valid = lead.first_name && lead.phone && lead.email;
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", {
        ...lead, bedrooms: `${bedrooms}`, comment:
          `Estimateur en ligne — ${bedrooms} ch. — zone ${zone} — estimation nette ${est.low}–${est.high} €/mois.`,
      });
      setSent(true);
      toast.success("Estimation envoyée ! Nous vous recontactons rapidement.");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <Layout>
      <Seo
        title="Estimation revenus Airbnb à Rouen | Simulateur gratuit | NJ Conciergerie"
        description="Estimez gratuitement les revenus locatifs de votre logement en courte durée à Rouen. Simulateur en ligne NJ Conciergerie, résultat immédiat et sans engagement."
        canonical="/estimation-revenus-airbnb-rouen" jsonLd={useLocalBusinessLd()} />

      <section className="bg-nj-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Propriétaires · Rouen</span>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl leading-[1.08]">Estimez les revenus Airbnb de votre logement à Rouen</h1>
          <p className="text-white/80 text-lg mt-4 max-w-2xl">Renseignez deux informations et obtenez une estimation immédiate du revenu net mensuel que votre bien pourrait générer en location courte durée.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-2 gap-10">
        {/* Simulator */}
        <div className="bg-white border border-nj-olive rounded-lg p-7" data-testid="estimator">
          <div className="flex items-center gap-2 text-nj-primary"><TrendingUp className="w-5 h-5" /><span className="font-semibold">Votre logement</span></div>
          <div className="mt-5 space-y-5">
            <div>
              <Label>Nombre de chambres</Label>
              <Select value={bedrooms} onValueChange={setBedrooms}>
                <SelectTrigger data-testid="est-bedrooms" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Studio</SelectItem>
                  <SelectItem value="1">1 chambre</SelectItem>
                  <SelectItem value="2">2 chambres</SelectItem>
                  <SelectItem value="3">3 chambres</SelectItem>
                  <SelectItem value="4">4 chambres ou +</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Secteur</Label>
              <Select value={zone} onValueChange={setZone}>
                <SelectTrigger data-testid="est-zone" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rive-droite">Rouen Rive Droite (centre)</SelectItem>
                  <SelectItem value="rive-gauche">Rouen Rive Gauche</SelectItem>
                  <SelectItem value="peripherie">Périphérie (Bois-Guillaume, Mont-Saint-Aignan…)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-7 bg-nj-olive/50 rounded-md p-6 text-center">
            <div className="text-sm text-nj-text2">Revenu net estimé (après gestion)</div>
            <div className="font-serif text-4xl text-nj-primary mt-2" data-testid="est-result">{est.low} € – {est.high} €<span className="text-lg text-nj-text2"> /mois</span></div>
            <p className="text-xs text-nj-muted mt-3 flex items-start gap-1.5 text-left"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Estimation indicative basée sur un taux d'occupation moyen. Le montant réel dépend du logement, des équipements et de la saison.</p>
          </div>
        </div>

        {/* Lead form */}
        <div>
          {sent ? (
            <div className="bg-white border border-nj-olive rounded-lg p-8 text-center h-full flex flex-col justify-center">
              <CheckCircle2 className="w-14 h-14 text-nj-primary mx-auto" />
              <h2 className="font-serif text-2xl text-nj-text mt-4">Merci !</h2>
              <p className="text-nj-text2 mt-2">Un conseiller NJ Conciergerie vous recontacte sous 24h avec une estimation personnalisée et gratuite.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-nj-olive rounded-lg p-7" data-testid="estimator-lead-form">
              <h2 className="font-serif text-2xl text-nj-text">Recevez votre estimation détaillée</h2>
              <p className="text-sm text-nj-text2 mt-1.5">Gratuit et sans engagement.</p>
              <div className="mt-5 space-y-4">
                <div><Label>Adresse du logement</Label><Input data-testid="est-address" value={lead.address} onChange={set("address")} className="mt-1.5" placeholder="N° et rue, Rouen" /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Code postal</Label><Input data-testid="est-postal" value={lead.postal_code} onChange={set("postal_code")} className="mt-1.5" placeholder="76000" inputMode="numeric" /></div>
                  <div><Label>Type de logement</Label>
                    <Select value={lead.property_type} onValueChange={setV("property_type")}>
                      <SelectTrigger data-testid="est-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Studio">Studio</SelectItem><SelectItem value="Appartement">Appartement</SelectItem><SelectItem value="Maison">Maison</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div><Label>Surface (m²)</Label><Input data-testid="est-surface" value={lead.surface} onChange={set("surface")} className="mt-1.5" inputMode="numeric" /></div>
                  <div><Label>Couchages</Label><Input data-testid="est-capacity" value={lead.capacity} onChange={set("capacity")} className="mt-1.5" inputMode="numeric" /></div>
                  <div><Label>Parking</Label>
                    <Select value={lead.parking} onValueChange={setV("parking")}>
                      <SelectTrigger data-testid="est-parking" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Prénom *</Label><Input required data-testid="est-firstname" value={lead.first_name} onChange={set("first_name")} className="mt-1.5" /></div>
                  <div><Label>Nom</Label><Input data-testid="est-lastname" value={lead.last_name} onChange={set("last_name")} className="mt-1.5" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Téléphone *</Label><Input required data-testid="est-phone" value={lead.phone} onChange={set("phone")} className="mt-1.5" /></div>
                  <div><Label>Email *</Label><Input required type="email" data-testid="est-email" value={lead.email} onChange={set("email")} className="mt-1.5" /></div>
                </div>
              </div>
              <Button type="submit" disabled={!valid || loading} data-testid="est-submit" className="w-full mt-6 bg-nj-primary hover:bg-nj-primaryHover text-white rounded-sm py-6 text-base inline-flex items-center gap-2">
                {loading ? "Envoi…" : <>Estimer gratuitement mes revenus <ArrowRight className="w-4 h-4" /></>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}
